<script lang="ts">
	import { onMount } from 'svelte';

	interface Props {
		playbackId: string;
		autoplay?: 'muted' | 'any' | null;
		loop?: boolean;
		muted?: boolean;
		wrapperClass?: string;
		rewindWhenOutOfView?: boolean;
		threshold?: number;
		maxResolution?: '720p' | '1080p' | '1440p' | '2160p';
		controls?: boolean;
		fit?: 'contain' | 'cover';
		objectPosition?: string;
		videoElement?: HTMLVideoElement;
		onloaded?: () => void;
		class?: string | string[];
		children?: (videoElement: HTMLVideoElement | undefined, loaded: boolean) => any;
	}

	let {
		playbackId,
		autoplay = 'muted',
		loop = true,
		muted = true,
		wrapperClass,
		rewindWhenOutOfView = true,
		threshold = 0,
		maxResolution = '1080p',
		controls = false,
		fit = 'cover',
		objectPosition = '50% 50%',
		videoElement = $bindable(),
		onloaded,
		class: className,
		children
	}: Props = $props();

	const playerPromise = import('@mux/mux-video');

	let wrapper: HTMLDivElement;

	let playOnLoad = $state(false);

	onMount(() => {
		const observer = new IntersectionObserver(
			(entries) => {
				const { isIntersecting } = entries[0];

				if (isIntersecting) {
					if (!videoElement) {
						playOnLoad = true;
						return;
					}
					if (videoElement.paused) {
						videoElement.play();
					}
					return;
				}

				if (!videoElement) return;

				if (!videoElement.paused) {
					videoElement.pause();
				}

				if (rewindWhenOutOfView) {
					videoElement.currentTime = 0;
				}
			},
			{ threshold }
		);

		observer.observe(wrapper);

		return () => {
			observer.disconnect();
		};
	});

	let loaded = $state(false);

	let placeholderSrcBase = $derived(`https://image.mux.com/${playbackId}/thumbnail`);
</script>

<div
	bind:this={wrapper}
	class="relative overflow-hidden{wrapperClass ? ` ${wrapperClass}` : ''}"
	style="content-visibility: auto;"
>
	{#await playerPromise then}
		<mux-video
			bind:this={videoElement}
			disable-tracking
			max-resolution={maxResolution}
			stream-type="on-demand"
			playback-id={playbackId}
			{autoplay}
			{loop}
			{muted}
			playsinline
			style={`--dialog: none; --media-object-position: ${objectPosition}; --media-object-fit: ${fit}; ${controls ? '' : '--controls: none;'}`}
			class={['relative', className]}
			onloadeddata={() => {
				loaded = true;
				onloaded?.();

				if (playOnLoad) {
					videoElement?.play();
				}
			}}
		></mux-video>
	{/await}

	{#if !loaded}
		<picture>
			{#if threshold > 0}
				<source
					media="(min-width: 2560px)"
					srcset={`${placeholderSrcBase}.webp?time=0&width=1920 2560w`}
					type="image/webp"
				/>
				<source
					media="(min-width: 1920px)"
					srcset={`${placeholderSrcBase}.webp?time=0&width=1536 1920w`}
					type="image/webp"
				/>
				<source
					media="(min-width: 1024px)"
					srcset={`${placeholderSrcBase}.webp?time=0&width=768 1024w`}
					type="image/webp"
				/>
				<source
					media="(min-width: 768px)"
					srcset={`${placeholderSrcBase}.webp?time=0&width=768 768w`}
					type="image/webp"
				/>
				<source
					media="(min-width: 640px)"
					srcset={`${placeholderSrcBase}.webp?time=0&width=640 640w`}
					type="image/webp"
				/>
				<source srcset={`${placeholderSrcBase}.webp?time=0&width=430 430w`} type="image/webp" />

				<source
					media="(min-width: 2560px)"
					srcset={`${placeholderSrcBase}.jpg?time=0&width=1920 2560w`}
					type="image/jpeg"
				/>
				<source
					media="(min-width: 1920px)"
					srcset={`${placeholderSrcBase}.jpg?time=0&width=1536 1920w`}
					type="image/jpeg"
				/>
				<source
					media="(min-width: 1024px)"
					srcset={`${placeholderSrcBase}.jpg?time=0&width=768 1024w`}
					type="image/jpeg"
				/>
				<source
					media="(min-width: 768px)"
					srcset={`${placeholderSrcBase}.jpg?time=0&width=768 768w`}
					type="image/jpeg"
				/>
				<source
					media="(min-width: 640px)"
					srcset={`${placeholderSrcBase}.jpg?time=0&width=640 640w`}
					type="image/jpeg"
				/>
				<source srcset={`${placeholderSrcBase}.jpg?time=0&width=430 430w`} type="image/jpeg" />

				<img
					alt=""
					loading={playOnLoad ? 'eager' : 'lazy'}
					class="absolute inset-0 h-full w-full bg-stone-4 object-cover"
					width="480"
					src={`${placeholderSrcBase}.jpg?time=0&width=480`}
				/>
			{:else}
				<source
					media="(min-width: 430px)"
					srcset={`${placeholderSrcBase}.webp?time=0&width=430 430w`}
					type="image/webp"
				/>
				<source srcset={`${placeholderSrcBase}.webp?time=0&width=320 320w`} type="image/webp" />

				<source
					media="(min-width: 430px)"
					srcset={`${placeholderSrcBase}.jpg?time=0&width=430 430w`}
					type="image/jpeg"
				/>
				<source srcset={`${placeholderSrcBase}.jpg?time=0&width=320 320w`} type="image/jpeg" />

				<img
					alt=""
					loading={playOnLoad ? 'eager' : 'lazy'}
					class="absolute inset-0 h-full w-full bg-stone-4 object-cover"
					width="320"
					src={`${placeholderSrcBase}.jpg?time=0&width=320`}
				/>
			{/if}
		</picture>
	{/if}

	{@render children?.(videoElement, loaded)}
</div>
